import { motion } from "framer-motion";
import {
  BookOpen,
  ChevronRight,
  Filter,
  FolderOpen,
  MousePointerClick,
  Search,
  Sparkles,
  Star,
} from "lucide-react";

const STEPS = [
  {
    icon: Search,
    title: "Search reports",
    body: "Use the search box in the left Reports panel to find a report by name or description. Matching categories expand automatically.",
  },
  {
    icon: FolderOpen,
    title: "Browse by category",
    body: "Expand Employee, Attendance, Leave, Payroll, Statutory, and other groups to see every report available for your organisation.",
  },
  {
    icon: MousePointerClick,
    title: "Open a report",
    body: "Click any report in the left panel. Its filters and workspace open here — nothing runs until you choose one.",
  },
  {
    icon: Filter,
    title: "Set filters & generate",
    body: "Pick the period, department, or other filters required for that report, then press Generate. Export to Excel, PDF, or CSV when ready.",
  },
  {
    icon: Star,
    title: "Pin favourites",
    body: "Star reports you use often. Favourites stay at hand so you can jump back without searching again.",
  },
];

export default function ReportsGuide({ recentReports = [], onSelectReport }) {
  return (
    <motion.section
      className="prw-guide"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      aria-labelledby="prw-guide-title"
    >
      <div className="prw-guide__hero">
        <div className="prw-guide__hero-icon" aria-hidden="true">
          <BookOpen size={28} />
        </div>
        <div>
          <p className="prw-guide__eyebrow">
            <Sparkles size={14} /> Getting started
          </p>
          <h1 id="prw-guide-title" className="prw-guide__title">
            How to use Reports
          </h1>
          <p className="prw-guide__lead">
            Select a report from the left sidebar to open it. Until then, this guide walks you through
            search, categories, filters, and exports — so you always know where to start.
          </p>
        </div>
      </div>

      <ol className="prw-guide__steps">
        {STEPS.map((step, i) => {
          const Icon = step.icon;
          return (
            <li key={step.title} className="prw-guide__step">
              <div className="prw-guide__step-num" aria-hidden="true">
                {i + 1}
              </div>
              <div className="prw-guide__step-icon" aria-hidden="true">
                <Icon size={18} />
              </div>
              <div>
                <h2 className="prw-guide__step-title">{step.title}</h2>
                <p className="prw-guide__step-body">{step.body}</p>
              </div>
            </li>
          );
        })}
      </ol>

      {recentReports.length > 0 ? (
        <div className="prw-guide__recent">
          <h2 className="prw-guide__recent-title">Continue with a recent report</h2>
          <div className="prw-guide__recent-list">
            {recentReports.slice(0, 4).map((r) => (
              <button
                key={r.key || r.href}
                type="button"
                className="prw-guide__recent-btn"
                onClick={() => onSelectReport?.(r)}
              >
                <span>{r.name}</span>
                <ChevronRight size={16} />
              </button>
            ))}
          </div>
        </div>
      ) : (
        <p className="prw-guide__hint">
          Tip: start with <strong>Employee Directory</strong> or <strong>Payroll Register</strong> from
          the left panel if you are exploring for the first time.
        </p>
      )}
    </motion.section>
  );
}
